/**
 * LaTeX Preview Component using KaTeX
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, Typography, Skeleton } from '@mui/material';
import type { LaTeXPreviewProps } from '../types';

const LaTeXPreview: React.FC<LaTeXPreviewProps> = ({
  latex,
  displayMode = false,
  throwOnError = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadKaTeX = async () => {
      try {
        setIsLoading(true);
        // Dynamically import KaTeX for code splitting
        await import('katex');
        await import('katex/dist/katex.min.css');

        if (!mounted) return;

        setIsLoaded(true);
        setError(null);
      } catch (err) {
        console.error('Failed to load KaTeX:', err);
        if (mounted) {
          setError('Failed to load math renderer');
          setIsLoaded(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadKaTeX();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !containerRef.current) return;

    const renderMath = async () => {
      try {
        const katex = await import('katex');

        if (!containerRef.current) return;

        // Clear previous content
        containerRef.current.innerHTML = '';

        if (!latex || latex.trim() === '') {
          containerRef.current.innerHTML = '<span style="color: #999; font-style: italic;">Empty expression</span>';
          return;
        }

        // Render the LaTeX expression
        katex.render(latex, containerRef.current, {
          displayMode,
          throwOnError,
          errorColor: '#cc0000',
          macros: {
            // Common macros for educational math
            '\\R': '\\mathbb{R}',
            '\\N': '\\mathbb{N}',
            '\\Z': '\\mathbb{Z}',
            '\\Q': '\\mathbb{Q}',
            '\\C': '\\mathbb{C}',
          },
          strict: false, // Allow some non-standard LaTeX
          trust: false, // Don't allow potentially dangerous commands
        });

        setError(null);
      } catch (err) {
        console.warn('LaTeX rendering error:', err);
        if (containerRef.current) {
          if (throwOnError) {
            setError(`Math rendering error: ${err instanceof Error ? err.message : String(err)}`);
          } else {
            // Show the raw LaTeX as fallback
            containerRef.current.innerHTML = `<code style="background: #f5f5f5; padding: 4px; border-radius: 4px; font-family: monospace; color: #666;">${latex}</code>`;
          }
        }
      }
    };

    renderMath();
  }, [latex, displayMode, throwOnError, isLoaded]);

  if (isLoading) {
    return (
      <Box className={className}>
        <Skeleton variant="text" width="60%" height={displayMode ? 60 : 24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        {error}
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <Box className={className}>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {latex || 'Empty expression'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        minHeight: displayMode ? 40 : 24,
        display: 'flex',
        alignItems: 'center',
        '& .katex': {
          fontSize: displayMode ? '1.2em' : '1em',
        },
        '& .katex-display': {
          margin: '0.5em 0',
        },
      }}
    />
  );
};

export default LaTeXPreview;